import { Link, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button.jsx'
import Breadcrumbs from '@/components/Breadcrumbs.jsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.jsx'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose } from '@/components/ui/sheet.jsx'
const VerificationFormEnhanced = lazy(() => import('@/components/VerificationFormEnhanced.jsx'))
import ThemeToggle from './ThemeToggle'
import { Menu, Home, Info, CreditCard, Users, Building, Briefcase, MapPin, HelpCircle, ChevronRight } from 'lucide-react'
import { tapCta } from '@/utils/analytics'
import authentipetLogo from '@/assets/authentipet-logo.png'

const NavLink = ({ to, children, isActive }) => (
  <Link
    to={to}
    className={`ap-body-text hover:text-ap-blurple transition-colors inline-flex items-center min-h-[44px] py-2 ${isActive ? 'text-ap-blurple' : 'text-white'
      }`}
    aria-current={isActive ? 'page' : undefined}
  >
    {children}
  </Link>
)

const MobileNavLink = ({ to, children, icon: Icon, onClick, isActive }) => (
  <Link
    to={to}
    onClick={onClick}
    className={`flex items-center justify-between p-4 rounded-xl transition-all ${isActive
        ? 'bg-ap-blurple/20 text-ap-blurple border border-ap-blurple/30'
        : 'text-ap-white hover:bg-white/5 border border-transparent'
      }`}
  >
    <div className="flex items-center gap-3">
      {Icon && <Icon className={`h-5 w-5 ${isActive ? 'text-ap-blurple' : 'text-ap-white/70'}`} />}
      <span className="font-medium text-lg">{children}</span>
    </div>
    <ChevronRight className={`h-5 w-5 ${isActive ? 'text-ap-blurple' : 'text-ap-white/30'}`} />
  </Link>
)

function Header() {
  const location = useLocation()
  const [animate, setAnimate] = useState(false)
  const [renterOpen, setRenterOpen] = useState(false)
  const [sheetOpen, setSheetOpen] = useState(false)

  useEffect(() => {
    // Trigger animation on route change
    setAnimate(true)
    const timer = setTimeout(() => setAnimate(false), 600)
    // Close mobile menu on route change
    setSheetOpen(false)
    return () => clearTimeout(timer)
  }, [location.pathname])

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true
    if (path !== '/' && location.pathname.startsWith(path)) return true
    return false
  }

  return (
    <>
      <header className="ap-gradient-header border-b border-ap-blurple/30 sticky top-0 z-50 backdrop-blur safe-top">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            {/* Mobile hamburger (top-left) */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  aria-label="Open navigation menu"
                  className="md:hidden p-2 -ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full hover:bg-white/10 text-ap-white transition-colors"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="preserve-white w-[300px] sm:w-[350px] bg-ap-darkblue border-r border-ap-blurple/30 p-0 flex flex-col">
                <SheetHeader className="p-6 border-b border-white/10">
                  <SheetTitle>
                    <Link
                      to="/"
                      onClick={() => setSheetOpen(false)}
                      className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ap-blurple focus-visible:ring-offset-2 focus-visible:ring-offset-ap-darkblue rounded-lg"
                    >
                      <img
                        src={authentipetLogo}
                        alt="Authentipet - Go to homepage"
                        className="h-10 w-auto"
                      />
                      <span className="ap-card-title text-ap-green text-xl">AUTHENTIPET</span>
                    </Link>
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto py-6 px-4 space-y-2">
                  <MobileNavLink to="/" icon={Home} onClick={() => setSheetOpen(false)} isActive={isActive('/')}>Home</MobileNavLink>
                  <MobileNavLink to="/how-it-works" icon={Info} onClick={() => setSheetOpen(false)} isActive={isActive('/how-it-works')}>How It Works</MobileNavLink>
                  <MobileNavLink to="/pricing" icon={CreditCard} onClick={() => setSheetOpen(false)} isActive={isActive('/pricing')}>Pricing</MobileNavLink>
                  <MobileNavLink to="/about" icon={Users} onClick={() => setSheetOpen(false)} isActive={isActive('/about')}>About Us</MobileNavLink>
                  <MobileNavLink to="/property-managers" icon={Building} onClick={() => setSheetOpen(false)} isActive={isActive('/property-managers')}>Property Managers</MobileNavLink>
                  <MobileNavLink to="/careers" icon={Briefcase} onClick={() => setSheetOpen(false)} isActive={isActive('/careers')}>Careers</MobileNavLink>
                  <MobileNavLink to="/locations" icon={MapPin} onClick={() => setSheetOpen(false)} isActive={isActive('/locations')}>Locations</MobileNavLink>
                  <MobileNavLink to="/faq" icon={HelpCircle} onClick={() => setSheetOpen(false)} isActive={isActive('/faq')}>FAQ</MobileNavLink>
                </div>

                <div className="p-6 border-t border-white/10 bg-black/20 safe-bottom">
                  <Button
                    className="btn-cta-pink w-full h-12 text-lg font-bold shadow-lg shadow-ap-pink/20"
                    onClick={() => {
                      tapCta('renter_cta', window.location.pathname, 'mobile_menu_footer');
                      setSheetOpen(false);
                      setTimeout(() => setRenterOpen(true), 150)
                    }}
                  >
                    VERIFY MY LETTER
                  </Button>
                </div>
              </SheetContent>
            </Sheet>

            <div className="flex items-center space-x-3">
              <Link
                to="/"
                className="flex items-center space-x-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ap-blurple focus-visible:ring-offset-2 focus-visible:ring-offset-ap-darkblue rounded-lg"
              >
                <img
                  src={authentipetLogo}
                  alt="Authentipet - Go to homepage"
                  className="h-12 w-auto"
                />
                <span className="ap-card-title text-ap-green hover:text-ap-blurple transition-colors">
                  AUTHENTIPET
                </span>
              </Link>
            </div>
            <ThemeToggle />
          </div>
          <nav className="hidden md:flex space-x-6">
            <NavLink to="/" isActive={isActive('/')}>Home</NavLink>
            <NavLink to="/how-it-works" isActive={isActive('/how-it-works')}>How It Works</NavLink>
            <NavLink to="/pricing" isActive={isActive('/pricing')}>Pricing</NavLink>
            <NavLink to="/about" isActive={isActive('/about')}>About Us</NavLink>
            <NavLink to="/property-managers" isActive={isActive('/property-managers')}>Property Managers</NavLink>
            <NavLink to="/careers" isActive={isActive('/careers')}>Careers</NavLink>
            <NavLink to="/locations" isActive={isActive('/locations')}>Locations</NavLink>
            <NavLink to="/faq" isActive={isActive('/faq')}>FAQ</NavLink>
          </nav>
          <Button
            className={`btn-cta-pink font-bold ${animate ? 'animate-wiggle' : ''} motion-reduce:animate-none active:opacity-80 focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:border-ring hidden md:inline-flex`}
            onClick={() => { tapCta('verify_letter_cta', window.location.pathname, 'header'); setRenterOpen(true) }}
          >
            VERIFY MY LETTER
          </Button>
          {/* Mobile CTA (small version) */}
          <Button
            className="btn-cta-pink md:hidden font-bold text-xs px-3 h-9"
            onClick={() => { tapCta('verify_letter_cta', window.location.pathname, 'header_mobile'); setRenterOpen(true) }}
          >
            VERIFY
          </Button>
        </div>

        {/* Renter Form Dialog */}
        <Dialog open={renterOpen} onOpenChange={setRenterOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-ap-green border border-white/20 text-ap-dark-green">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-ap-dark-green">Score Your ESA Letter</DialogTitle>
              <p className="text-ap-dark-green/90 mt-2">Get your Emotional Support Animal letter scored for authenticity</p>
            </DialogHeader>
            <div className="pt-4">
              <Suspense fallback={<div className="text-center p-8 text-ap-dark-green/70">Loading form...</div>}>
                <VerificationFormEnhanced
                  onSuccess={() => setRenterOpen(false)}
                />
              </Suspense>
            </div>
          </DialogContent>
        </Dialog>
      </header>
      {/* Mobile breadcrumb bar (sticky, under header) */}
      <div className="sm:hidden sticky top-safe z-40 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-white/10">
        <div className="container mx-auto px-4 py-2">
          <Breadcrumbs />
        </div>
      </div>
      {/* Desktop breadcrumb bar (non-sticky) */}
      <div className="hidden sm:block bg-transparent">
        <div className="container mx-auto px-4 py-2">
          <Breadcrumbs />
        </div>
      </div>
    </>
  )
}

export default Header

